import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import { useDebounce } from '../../../hooks/useDebounce'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { useChangeMutations } from '../hooks/useChangeMutations'
import { EMPTY_CHANGE_FILTER, changeAffordances, isChangeFiltered, toChangeFilter, type ChangeFilterState } from '../lib/changes'
import { describeCostControlError } from '../lib/errors'
import { moneyLocale } from '../lib/money'
import type { CostChange, CostContract, CostControlView } from '../types'
import ChangeDialog from './ChangeDialog'
import ChangeFromRfqDialog from './ChangeFromRfqDialog'
import ChangesTable from './ChangesTable'
import ChangesTabSkeleton from './ChangesTabSkeleton'
import ChangesToolbar from './ChangesToolbar'

interface Props { projectId: string }

const ChangesTab: React.FC<Props> = ({ projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { history } = useRouter()
  const actions = useModuleActions('COST_CONTROL', projectId)
  const af = changeAffordances(actions, null)
  const mutations = useChangeMutations(projectId)
  const [filter, setFilter] = useState<ChangeFilterState>(EMPTY_CHANGE_FILTER)
  const [search] = useDebounce(filter.search, 300)
  const [creating, setCreating] = useState(false)
  const [fromRfq, setFromRfq] = useState(false)
  const { data, loading, error } = useQuery<{ costChanges: CostChange[] }>(Query.COST_CHANGES, {
    variables: { projectId, filter: toChangeFilter(filter, search) },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: contractData } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: {} },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const { data: viewData } = useQuery<{ costControl: CostControlView }>(Query.COST_CONTROL, {
    variables: { projectId },
    fetchPolicy: 'cache-first',
    context: { handlesErrors: true },
  })
  const currency = viewData?.costControl.settings.currency ?? 'CZK'
  const contracts = contractData?.costContracts ?? []
  const rows = data?.costChanges
  const open = (id: string) => history.push(`/project/${projectId}/cost-control/changes/${id}`)

  if (!rows && loading) return <ChangesTabSkeleton />

  return (
    <div className="space-y-2">
      <ChangesToolbar count={rows?.length ?? 0} filter={filter} onFilterChange={setFilter} contracts={contracts} canCreate={af.create} onCreate={() => setCreating(true)} onCreateFromRfq={() => setFromRfq(true)} />
      {!rows && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
      {rows && rows.length === 0 && (
        <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
          {t(isChangeFiltered(filter) ? 'CostControl_changes_empty_filtered' : 'CostControl_changes_empty')}
        </div>
      )}
      {rows && rows.length > 0 && <ChangesTable rows={rows} currency={currency} locale={locale} onOpen={open} />}
      <ChangeDialog
        open={creating}
        mode="create"
        contracts={contracts}
        onOpenChange={setCreating}
        onSubmit={async (input) => {
          const res = await mutations.create(input)
          if (res.ok) open(res.data.createCostChange.id)
          return res.ok
        }}
      />
      {fromRfq && <ChangeFromRfqDialog projectId={projectId} currency={currency} locale={locale} onClose={() => setFromRfq(false)} />}
    </div>
  )
}
export default ChangesTab
